/**
 * DisputesInbox — /w/:slug/app/settings/disputes
 *
 * PR-Cluster-6-i (docs/ux-design-plan.md §10). Workspace-wide
 * landing surface for every active assessor-annotation dispute in
 * the workspace. Owners' answer to "what's on our plate that the
 * assessors haven't acknowledged yet?"
 *
 * Read-only + self-hides when the workspace has no active disputes
 * (a workspace that's never disputed anything doesn't want a
 * permanent empty rail). Each row deep-links to the register-item
 * page where the workspace member can withdraw or the assessor can
 * re-annotate.
 *
 * Backend at packages/cloudflare/src/routes/assessor-annotations.ts
 * (GET /disputes). Enriched shape returns annotation + register-item
 * context so this page renders per-row without follow-up round-trips.
 */

import { Link } from 'react-router-dom';
import { Alert, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  listWorkspaceDisputes,
  type AssessorAnnotationDisputeInboxRow,
  type AssessorAnnotationVerdict,
} from '../lib/api';
import { fmtDate } from '../lib/format';

export function DisputesInbox() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const disputes = useWorkspaceQuery(
    (slug) => queryKeys.disputes(slug),
    (slug) => listWorkspaceDisputes(slug),
  );

  if (!slug) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '68rem' }}>
      <PageHeader
        title="Assessor disputes"
        subtitle="Active workspace counter-views on 3PAO verdicts. Withdraw or wait for the assessor to acknowledge or re-annotate."
      />

      {disputes.loading ? (
        <Spinner label="Loading disputes" />
      ) : disputes.error ? (
        <Alert variant="error">{disputes.error}</Alert>
      ) : !disputes.data || disputes.data.disputes.length === 0 ? (
        <EmptyState
          title="No active disputes"
          description="When your workspace files a dispute on an assessor's verdict, it lands here until the assessor acknowledges it, re-annotates the control, or the workspace withdraws it."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {disputes.data.disputes.map((d) => (
            <DisputeRow key={d.dispute_id} slug={slug} row={d} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Row ------------------------------------------------------------

function DisputeRow({
  slug,
  row,
}: {
  slug: string;
  row: AssessorAnnotationDisputeInboxRow;
}) {
  const registerItemHref = `/w/${slug}/app/register/${row.annotation_register_item_id}`; // scope-check-ignore: workspace-scoped shape is dynamic + validated by workspaceContext
  return (
    <article
      style={{
        border: '1px solid var(--band-amber)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
        background: 'var(--warning-bg)',
        color: 'var(--warning-fg)',
        padding: 'var(--space-4) var(--space-5)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <header
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'baseline',
          flexWrap: 'wrap',
        }}
      >
        <VerdictPill verdict={row.disputed_verdict} />
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: 'var(--text)',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Link
            to={registerItemHref}
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              textDecorationColor: 'var(--border-strong)',
            }}
          >
            {row.register_item_title ?? row.annotation_register_item_id}
          </Link>
        </h2>
        <time
          dateTime={row.created_at}
          style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}
        >
          Filed {fmtDate(row.created_at)}
        </time>
      </header>

      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <div>
          <span
            style={{
              fontSize: 'var(--font-size-3xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-muted)',
              fontWeight: 700,
              marginRight: 'var(--space-2)',
            }}
          >
            Workspace counter-view
          </span>
          <span
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-dim)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            filed by {row.disputed_by_user_id}
          </span>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--warning-fg)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {row.dispute_reason_text}
        </p>
      </div>

      <details style={{ marginTop: 'var(--space-2)' }}>
        <summary
          style={{
            cursor: 'pointer',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-muted)',
            listStyle: 'none',
          }}
        >
          <span aria-hidden style={{ marginRight: 'var(--space-2)', color: 'var(--text-dim)' }}>
            ▸
          </span>
          Show assessor's original reason
        </summary>
        <div
          style={{
            marginTop: 'var(--space-2)',
            padding: 'var(--space-3)',
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              alignItems: 'baseline',
              marginBottom: '4px',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-3xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-muted)',
                fontWeight: 700,
              }}
            >
              Assessor
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-3xs)',
                color: 'var(--text-muted)',
              }}
              title={row.annotation_annotated_by}
            >
              {row.annotation_annotated_by}
            </span>
            <time
              dateTime={row.annotation_annotated_at}
              style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}
            >
              · annotated {fmtDate(row.annotation_annotated_at)}
            </time>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {row.annotation_reason_text}
          </p>
        </div>
      </details>

      <footer
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span
          title={row.disputed_engagement_id}
          style={{ fontFamily: 'var(--font-mono)' }}
        >
          engagement {row.disputed_engagement_id.slice(0, 12)}…
        </span>
        <span aria-hidden>·</span>
        <span title={row.filing_event_id} style={{ fontFamily: 'var(--font-mono)' }}>
          audit {row.filing_event_id.slice(0, 12)}…
        </span>
        <Link
          to={registerItemHref}
          style={{
            marginLeft: 'auto',
            color: 'var(--accent)',
            fontWeight: 500,
            textDecoration: 'none',
          }}
        >
          Open register item →
        </Link>
      </footer>
    </article>
  );
}

// ---- Verdict pill (matches AssessorAnnotationsSection tone) --------

const VERDICT_TONE: Record<
  AssessorAnnotationVerdict,
  { label: string; bg: string; fg: string; border: string }
> = {
  accepted: {
    label: 'Accepted',
    bg: 'var(--band-green)',
    fg: 'var(--text-inverse)',
    border: 'var(--band-green)',
  },
  needs_remediation: {
    label: 'Needs remediation',
    bg: 'var(--band-red)',
    fg: 'var(--text-inverse)',
    border: 'var(--band-red)',
  },
  not_applicable: {
    label: 'Not applicable',
    bg: 'var(--surface-alt)',
    fg: 'var(--text-muted)',
    border: 'var(--border)',
  },
};

function VerdictPill({ verdict }: { verdict: AssessorAnnotationVerdict }) {
  const t = VERDICT_TONE[verdict];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
      }}
    >
      {t.label}
    </span>
  );
}
